import app from "flarum/forum/app";
import Button from 'flarum/common/components/Button';
import Modal from 'flarum/common/components/Modal';
import icon from "flarum/common/helpers/icon";
import KeyboardNavigatable from 'flarum/common/utils/KeyboardNavigatable';
import classList from 'flarum/common/utils/classList';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Product from '../../../../../../../core/js/src/common/models/Product';

import type { IInternalModalAttrs } from 'flarum/common/components/Modal';
import type Mithril from 'mithril';

export interface IProductSelectionModalAttrs extends IInternalModalAttrs {
  className?: string;
  title?: string;
  canSelectMultiple?: boolean;
  selectedProducts?: Product[],
  onsubmit?: (selected: Product[]) => void;
}

/**
 *  产品选择弹框
 */
export default class ProductDiscussionModal<CustomAttrs extends IProductSelectionModalAttrs = IProductSelectionModalAttrs> extends Modal<CustomAttrs> {
  protected loading = true;
  protected focused = false;
  // 产品列表
  protected list!: Product[];
  // input
  protected navigator = new KeyboardNavigatable();
  // 当前一级tag
  protected firstActive = null;
  // 当前二级tag
  protected secondActive = null;
  // 被选择的产品
  protected selected: Product[] = [];

  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    app.productList.load().then(products => {
      this.loading = false;

      this.list = products;

      if (this.attrs.selectedProducts) {
        this.attrs.selectedProducts.map(this.addProduct.bind(this));
      }

      this.setInitList();

      m.redraw();
    });

    // this.navigator
    //   .onRemove(() => this.selected.splice(this.selected.length - 1, 1));
  }

  className() {
    return classList('ProductSelectionModal ProductDiscussionModal', this.attrs.className);
  }

  title() {
    return app.translator.trans('acme-ruko.forum.product.choose_title');
  }

  content() {
    if (this.loading || !this.list) {
      return <LoadingIndicator />;
    }

    const firstTags = this.getFirstTag();
    const secondTags = this.getSecondTag();
    const products = this.getProductList();

    const canSimple = this.attrs.canSelectMultiple;
    const selectedLen = this.selected.length === 1;

    return [
      <div className="Modal-body">
        <div className="ProductDiscussionModal-form">
          <div className="ProductDiscussionModal-form-input">
            <div className={'ProductInput FormControl ' + (this.focused ? 'focus' : '')} onclick={() => this.$('.ProductInput input').focus()}>
              <span className="ProductInput-selected">
                {this.selected.map((product) => (
                  <span className="ProductInput-tag">
                    {product.name()}
                    <span
                      onclick={() => {
                        this.removeProduct(product);
                        this.onready();
                      }}
                    >
                      {icon('fas fa-times')}
                    </span>
                  </span>
                ))}
              </span>
              {/*<input*/}
              {/*  className="FormControl"*/}
              {/*  style={{ width: 0 + 'ch' }}*/}
              {/*  onkeydown={this.navigator.navigate.bind(this.navigator)}*/}
              {/*  onfocus={() => (this.focused = true)}*/}
              {/*  onblur={() => (this.focused = false)}*/}
              {/*/>*/}
            </div>
          </div>
          <div className="ProductDiscussionModal-form-submit App-primaryControl">
            <Button
              type="submit"
              className="Button Button--primary"
              icon="fas fa-check"
            >
              {app.translator.trans('flarum-tags.lib.tag_selection_modal.submit_button')}
            </Button>
          </div>
        </div>
      </div>,

      <div className="Modal-footer">
        <div className="ProductDiscussionModal-list SelectProductList">
          <ul className="SelectProductList-tag">
            {firstTags.map(tag => (
              <li
                datatype="tags"
                data-index={tag.id}
                className={classList('SelectProductList-tag-first',{
                  active: this.firstActive?.id === tag.id,
                })}
                onmouseover={() => (this.firstActive?.id === tag.id)}
                onclick={this.onActiveFirstTag.bind(this, tag)}
              >
                {tag.name}
              </li>
            ))}
          </ul>
          <ul className="SelectProductList-tag SelectProductList-seriesTag">
            {secondTags.map(tag => (
              <div className="SelectProductList-series">
                <li
                  datatype="tags"
                  data-index={tag.id}
                  className={classList('SelectProductList-tag-second',{
                    active: this.secondActive?.id === tag.id,
                  })}
                  onmouseover={() => (this.secondActive?.id === tag.id)}
                  onclick={this.onActiveSecondTag.bind(this, tag)}
                >
                  {tag.name}
                </li>
                <ul className="SelectProductList-series-product SelectProductList-product">
                  {products.map(product => (
                    <li
                      datatype="product"
                      data-index={product.id}
                      className={classList({
                        selected: this.selected.includes(product),
                        isSimple: !canSimple && selectedLen && !this.selected.includes(product),
                      })}
                      onclick={this.onSelectProduct.bind(this, product)}
                    >
                      <span className="SelectProductList-product-icon">
                        {this.selected.includes(product) ? icon('fas fa-check') : ''}
                      </span>
                      <span>{product.name()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </ul>
        </div>
      </div>,
    ];
  }

  // 设置出始列表
  protected setInitList(isIndex = true, tag?) {
    const firstTags = this.getFirstTag();

    if (firstTags.length) {
      this.firstActive = isIndex ? firstTags[0] : tag;
    }
    const secondTag = this.getSecondTag();
    if (secondTag.length) {
      this.secondActive = secondTag[0];
    }
    this.getProductList();
  }

  // 获取第一栏tag
  protected getFirstTag() {
    const firstTags = this.list.map(product => product.data.attributes.firstTag);

    return Array.from(new Map(firstTags.map(tag => [tag.id, tag])).values());
  }

  // 获取当前一级tag下的二级tag
  protected getSecondTag() {
    const secondTag = this.list.map(product => product.data.attributes.secondTag)
                          .filter(tag => tag.parent.id === this.firstActive.id);

    return Array.from(new Map(secondTag.map(tag => [tag.id, tag])).values());
  }

  // 获取当前二级tag下的产品列表
  protected getProductList() {
    return  this.list.filter(item => item.data.attributes.secondTag.id === this.secondActive.id);
  }

  // 切换第一栏tag
  protected onActiveFirstTag(tag) {
    this.setInitList(false, tag);
  }

  // 切换第二栏tag
  protected onActiveSecondTag(tag) {
    this.secondActive = tag;
    this.getProductList();
  }

  // 添加产品
  protected addProduct(product) {
    if (!product) return;

    if (!this.selected.includes(product)) {
      this.selected.push(product);
    }
  }

  // 移除产品
  protected removeProduct(product) {
    const index = this.selected.indexOf(product);

    if (index !== -1) {
      this.selected.splice(index, 1);
    }
  }

  // 选择产品
  protected onSelectProduct(product) {
    const isSimple = this.attrs.canSelectMultiple;
    const len = this.selected.length === 1;
    const exist = this.selected.includes(product);

    if (!isSimple && len && !exist) return;

    if (exist) {
      this.removeProduct(product);
    } else {
      this.addProduct(product);
    }
  }

  onsubmit(e) {
    e.preventDefault();

    if (this.attrs.onsubmit) this.attrs.onsubmit(this.selected);

    this.hide();
  }
}
